import { ChevronLeft, FileText, Link2, Link2Off, Tag, FilePlus, Ban } from 'lucide-react';
import { ACCENT, OK, BAD, WARN } from '../../lib/uiTokens';
import { fmt, fmtDate, zobrazProtistranu, STAV_LABEL, STAV_BARVA, KAT_LABEL } from './bankaSdilene';
import ZpetTlacitko from '../../components/ui/ZpetTlacitko';

const TYP_PAROVANI = {
    automatic_exact: 'auto (VS / QR kód)',
    automatic_karta: 'auto (karta + den + částka)',
    automatic_fuzzy: 'auto',
    manual: 'ručně',
};

/**
 * Prostřední sloupec Banky — detail jednoho pohybu.
 *
 * **Tohle je ta nová věc (3.481).** Do té doby pohyb detail neměl: párování
 * žilo v modalu a zařazení v rozbalovacím menu přímo v řádku tabulky. Nešlo
 * se proklikat pohyb za pohybem a řádek kvůli tomu nesl tři ovladače.
 *
 * Akce zůstávají modaly — jsou to krátké věci s začátkem a koncem, u kterých
 * je pozadí nepodstatné (norma §1f: rychlá akce a nastavení = modal, editace
 * vybrané položky = tenhle sloupec).
 */
export default function PohybDetail({
    transakce: t, cardStyle, isMobile = false, onZpet,
    onParovat, onZaradit, onVytvoritDoklad, onOdpojit, onIgnorovat, onOpenDoklad,
    rezervaDole = null,
}) {
    if (!t) return null;

    const prichozi = t.smer === 'prichozi';
    const barvaStavu = STAV_BARVA[t.stav_parovani] || 'var(--text-tertiary)';
    const doklady = t.doklady || [];
    const alokovano = doklady.reduce((a, d) => a + (d.castka_alokovana || 0), 0);
    const zbyva = Math.round((t.castka - alokovano) * 100) / 100;

    const Nadpis = ({ children }) => (
        <div className="text-[9.5px] font-bold uppercase mt-5 mb-2"
            style={{ letterSpacing: '0.12em', color: 'var(--text-secondary)' }}>{children}</div>
    );
    const Radek = ({ popis, hodnota }) => (
        <div className="flex items-baseline justify-between gap-3 py-[7px]" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
            <span className="text-[12px] flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>{popis}</span>
            <span className="text-[12.5px] text-right truncate">{hodnota ?? '—'}</span>
        </div>
    );
    const Akce = ({ Icon, label, onClick, hlavni }) => (
        <button onClick={onClick}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-[10px]"
            style={{
                minHeight: isMobile ? 48 : 40, padding: '0 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                background: hlavni ? ACCENT : 'var(--bg-secondary)',
                color: hlavni ? '#fff' : 'var(--text-primary)',
                border: hlavni ? 'none' : '1px solid var(--border-default)',
            }}>
            <Icon size={15} /> {label}
        </button>
    );

    return (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-2xl flex flex-col flex-1 min-h-0'}
            style={isMobile ? null : cardStyle}>

            {isMobile && (
                <div className="flex items-center gap-2 px-4 flex-shrink-0" style={{ height: 52 }}>
                    <ZpetTlacitko onClick={onZpet} popis="Zpět na pohyby" />
                </div>
            )}

            <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: isMobile ? '0 16px' : 18, paddingBottom: isMobile ? (rezervaDole || 16) : 18 }}>

                <div className="flex items-start justify-between gap-3 pb-3.5" style={{ borderBottom: '1px solid var(--border-default)' }}>
                    <div className="min-w-0">
                        <div className="text-[26px] font-extrabold tabular-nums leading-none"
                            style={{ color: prichozi ? OK : BAD }}>
                            {prichozi ? '+' : '−'}{fmt(t.castka)} Kč
                        </div>
                        <div className="text-[13px] mt-1.5 truncate">{zobrazProtistranu(t)}</div>
                        <div className="text-[11.5px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                            {fmtDate(t.datum)}
                            {t.variabilni_symbol ? ` · VS ${t.variabilni_symbol}` : ''}
                            {t.protistrana_ucet ? ` · ${t.protistrana_ucet}${t.protistrana_banka ? '/' + t.protistrana_banka : ''}` : ''}
                        </div>
                    </div>
                    <span className="flex-shrink-0" style={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                        padding: '4px 9px', borderRadius: 5, color: barvaStavu,
                        background: `${barvaStavu}1f`, border: `1px solid ${barvaStavu}55`, whiteSpace: 'nowrap',
                    }}>{STAV_LABEL[t.stav_parovani]}</span>
                </div>

                {/* Napárované doklady — jedna platba může nést víc dokladů (alokace) */}
                {doklady.length > 0 && (
                    <>
                        <Nadpis>Napárované doklady · {doklady.length}</Nadpis>
                        {doklady.map(d => (
                            <div key={d.id} className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] mb-1.5"
                                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}>
                                <FileText size={15} style={{ color: ACCENT, flexShrink: 0 }} />
                                <button onClick={() => onOpenDoklad?.(d.id)} className="flex-1 min-w-0 text-left">
                                    <div className="text-[12.5px] font-semibold truncate" style={{ color: ACCENT }}>
                                        {d.interni_cislo || `#${d.id}`}{d.dodavatel ? ` · ${d.dodavatel}` : ''}
                                    </div>
                                    <div className="text-[10.5px]" style={{ color: 'var(--text-secondary)' }}>
                                        alokováno {fmt(d.castka_alokovana)} z {fmt(d.castka_s_dph)} Kč
                                        {d.typ_parovani ? ` · ${TYP_PAROVANI[d.typ_parovani] ?? d.typ_parovani}` : ''}
                                    </div>
                                </button>
                            </div>
                        ))}
                        {Math.abs(zbyva) > 0.005 && (
                            <div className="text-[11.5px] mt-1" style={{ color: WARN }}>
                                Nerozdělený zbytek platby: {fmt(zbyva)} Kč
                            </div>
                        )}
                    </>
                )}

                {t.tag && !doklady.length && (
                    <>
                        <Nadpis>Zařazení</Nadpis>
                        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px]"
                            style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}>
                            <Tag size={14} style={{ color: ACCENT }} />
                            <span className="text-[12.5px]">{KAT_LABEL[t.tag] || t.tag}</span>
                        </div>
                        <div className="text-[11px] mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                            Platba bez dokladu — do Výsledovky vstupuje přes tohle zařazení.
                        </div>
                    </>
                )}

                <Nadpis>Co s tím</Nadpis>
                <div className="flex flex-wrap gap-2">
                    <Akce Icon={Link2} label={doklady.length ? 'Upravit párování' : 'Spárovat s dokladem'} onClick={onParovat} hlavni={!doklady.length && !t.tag} />
                    <Akce Icon={Tag} label={doklady.length ? 'Zařadit místo dokladu' : t.tag ? 'Změnit zařazení' : 'Zařadit na kategorii'} onClick={onZaradit} />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                    <Akce Icon={FilePlus} label="Vytvořit doklad z platby" onClick={onVytvoritDoklad} />
                    {doklady.length > 0
                        ? <Akce Icon={Link2Off} label="Odpojit doklady" onClick={onOdpojit} />
                        : <Akce Icon={Ban} label="Označit nepárovatelné" onClick={onIgnorovat} />}
                </div>

                <Nadpis>Z výpisu</Nadpis>
                <Radek popis={t.datum_transakce ? 'Zaúčtováno' : 'Datum'} hodnota={fmtDate(t.datum)} />
                {/* U karty dává ČS den nákupu zvlášť — o 1–3 dny dřív než zaúčtování,
                    a právě podle něj se hledá účtenka. */}
                {t.datum_transakce && <Radek popis="Den nákupu" hodnota={fmtDate(t.datum_transakce)} />}
                <Radek popis="Směr" hodnota={prichozi ? 'příchozí' : 'odchozí'} />
                {t.je_kartova && t.karta_posledni4 && <Radek popis="Karta" hodnota={`•••${t.karta_posledni4}`} />}
                {/* Odchozí převod: ČS sem dává prvních 20 znaků zprávy pro příjemce. */}
                {!t.je_kartova && t.protistrana_nazev && <Radek popis={prichozi ? 'Protistrana' : 'Zpráva / příjemce'} hodnota={t.protistrana_nazev} />}
                <Radek popis="Protiúčet" hodnota={t.protistrana_ucet ? `${t.protistrana_ucet}${t.protistrana_banka ? '/' + t.protistrana_banka : ''}` : null} />
                <Radek popis="Variabilní symbol" hodnota={t.variabilni_symbol} />
                {t.konstantni_symbol && <Radek popis="Konstantní symbol" hodnota={t.konstantni_symbol} />}
                {t.specificky_symbol && !t.je_kartova && <Radek popis="Specifický symbol" hodnota={t.specificky_symbol} />}
                {t.cislo_dokladu && <Radek popis="Doklad banky" hodnota={t.cislo_dokladu} />}
                {t.poznamka && <Radek popis="Poznámka" hodnota={t.poznamka} />}
            </div>
        </div>
    );
}
